import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import './landing-page.css';
import {
  ArrowRight,
  c,
  Container,
  Reveal,
  SiteFooter,
  SiteHeader,
  useScrollToTopOnMount,
} from './mrigl-shell';
import {MriglPacks} from './mrigl-packs';
import {
  MRIGL_SALES_CONDITIONS,
  MRIGL_TAX_AND_FEES_INFO,
} from './company-info';

export function MriglPricingPage() {
  useScrollToTopOnMount();
  return (
    <div
      className="mrigl-landing min-h-screen"
      style={{background: c.bg, color: c.ink}}
    >
      <DefaultMetaTags />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-16 focus:top-16 focus:z-50 focus:rounded focus:bg-black focus:px-16 focus:py-8 focus:text-white"
      >
        Aller au contenu
      </a>
      <SiteHeader />
      <main id="main">
        <PricingHero />
        <PricingTable />
        <PricingTrust />
        <PricingFaq />
        <PricingFinalCta />
      </main>
      <SiteFooter />
    </div>
  );
}

/* ---------------- Hero ---------------- */

function PricingHero() {
  return (
    <section
      className="relative pb-48 pt-64 md:pb-64 md:pt-96"
      aria-labelledby="pricing-hero-title"
    >
      <Container className="text-center">
        <motion.div
          initial={{opacity: 0, y: 12}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.55, ease: [0.22, 0.61, 0.36, 1]}}
          className="mx-auto max-w-720"
        >
          <span className="eyebrow mb-24 inline-flex items-center gap-10">
            <span className="inline-block h-8 w-8 lime-mark" aria-hidden="true" />
            Tarif
          </span>

          <h1
            id="pricing-hero-title"
            className="display mb-24 text-5xl md:text-7xl"
            style={{lineHeight: 1.04}}
          >
            <span className="gradient-text">Un prix. Un an.</span>
            <br />
            Pas de surprises.
          </h1>

          <p
            className="mx-auto max-w-560 text-lg leading-7"
            style={{color: c.inkMuted}}
          >
            Tout ce qu'il vous faut pour mettre votre site en ligne — pour un
            seul prix annuel. Pas de plans à plusieurs niveaux. Pas
            d'augmentation au renouvellement.
          </p>
        </motion.div>
      </Container>
    </section>
  );
}

/* ---------------- Packs (dynamic from admin) ---------------- */

function PricingTable() {
  return (
    <section
      className="py-32 md:py-48"
      aria-labelledby="pricing-table-title"
    >
      <Container>
        <h2 id="pricing-table-title" className="sr-only">
          Formules
        </h2>
        <Reveal>
          <MriglPacks loader="pricingPage" variant="full" />
        </Reveal>

        <p
          className="mx-auto mt-32 max-w-560 text-center text-sm"
          style={{color: c.inkMuted, lineHeight: 1.6}}
        >
          Tous les prix affichés sont TTC. Les taxes et tous les frais sont
          inclus : aucun frais supplémentaire n'est ajouté au paiement.
        </p>

        <Reveal>
          <div className="commercial-info-card mx-auto mt-28 max-w-760">
            <h3>Informations commerciales</h3>
            <dl>
              <div>
                <dt>Devise</dt>
                <dd>TND — dinar tunisien</dd>
              </div>
              <div>
                <dt>Détails des taxes</dt>
                <dd>{MRIGL_TAX_AND_FEES_INFO.taxDetails}</dd>
              </div>
              <div>
                <dt>Frais supplémentaires</dt>
                <dd>{MRIGL_TAX_AND_FEES_INFO.extraFees}</dd>
              </div>
              <div>
                <dt>Conditions de livraison</dt>
                <dd>{MRIGL_TAX_AND_FEES_INFO.deliveryTerms}</dd>
              </div>
              <div>
                <dt>Conditions générales de vente</dt>
                <dd>
                  <Link to={MRIGL_SALES_CONDITIONS.url}>
                    Accessibles avant paiement
                  </Link>
                  {' — '}
                  {MRIGL_SALES_CONDITIONS.accessibleBeforePayment}
                </dd>
              </div>
            </dl>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

/* ---------------- Trust ---------------- */

function PricingTrust() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="trust-title"
    >
      <Container className="grid gap-48 md:grid-cols-[5fr_7fr] md:gap-80">
        <Reveal className="md:sticky md:top-96 md:self-start">
          <span className="eyebrow mb-20 inline-block">Ce que vous payez</span>
          <h2
            id="trust-title"
            className="display mb-16 text-3xl md:text-5xl"
            style={{maxWidth: '14ch', lineHeight: 1.05}}
          >
            Inclus dans le prix annuel.
          </h2>
          <p style={{color: c.inkMuted, maxWidth: '34ch'}}>
            Une vraie liste — sans options cachées, sans modules payants.
          </p>
        </Reveal>

        <Reveal>
          <ul className="included-list m-0 list-none p-0">
            {[
              ['Hébergement inclus', 'votre site est en ligne, point.'],
              [
                'Sous-domaine gratuit',
                'votresite.mrigl.site, prêt à l’emploi.',
              ],
              [
                'Connexion à votre nom de domaine',
                'utilisez le vôtre, ou pas.',
              ],
              ['SSL inclus', 'connexion sécurisée par défaut.'],
              [
                'Modèles adaptés au mobile',
                'votre site fonctionne sur tous les écrans.',
              ],
              ['Formulaire de contact', 'qui marche vraiment.'],
              ['Éditeur simple', 'utilisable par tout le monde.'],
              [
                'Mises à jour incluses',
                'au fur et à mesure qu’on améliore l’outil.',
              ],
            ].map(([lead, body]) => (
              <li
                key={lead}
                className="grid grid-cols-[1.4rem_1fr] items-start gap-16 py-22"
              >
                <span
                  className="mt-10 inline-block h-8 w-8 lime-mark"
                  aria-hidden="true"
                />
                <span style={{color: c.ink2, lineHeight: 1.55}}>
                  <b style={{fontWeight: 600, color: c.ink}}>{lead}</b> — {body}
                </span>
              </li>
            ))}
          </ul>

          <p
            className="mt-26 max-w-480 text-sm"
            style={{color: c.inkMuted, lineHeight: 1.6}}
          >
            La génération de modèles par IA arrivera plus tard, et restera
            incluse pour les abonnés existants.
          </p>
        </Reveal>
      </Container>
    </section>
  );
}

/* ---------------- FAQ ---------------- */

const faqs: [string, string][] = [
  [
    'Est-ce que le prix change au renouvellement ?',
    'Non. Le prix que vous payez aujourd’hui est celui que vous paierez l’année prochaine, et l’année suivante.',
  ],
  [
    'Puis-je connecter mon propre nom de domaine ?',
    'Oui. Vous pouvez utiliser un domaine que vous possédez déjà, ou rester sur votresite.mrigl.site — c’est inclus dans tous les cas.',
  ],
  [
    'Et si MRIGL.site ne convient pas à mon projet ?',
    'On vous le dira. Si votre projet a besoin d’une boutique, d’un blog volumineux ou d’une appli sur mesure, on n’est pas le bon outil — et on vous le signalera honnêtement avant que vous payiez.',
  ],
  [
    'Comment se passe le paiement ?',
    'Paiement annuel sécurisé en ligne. Les prix sont TTC, taxes et frais inclus, sans aucun frais supplémentaire. Pas d’abonnement caché ni de prélèvement répété sans préavis.',
  ],
];

function PricingFaq() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="faq-title"
    >
      <Container className="grid gap-48 md:grid-cols-[5fr_7fr] md:gap-80">
        <Reveal className="md:sticky md:top-96 md:self-start">
          <span className="eyebrow mb-20 inline-block">Questions</span>
          <h2
            id="faq-title"
            className="display mb-16 text-3xl md:text-5xl"
            style={{maxWidth: '14ch', lineHeight: 1.05}}
          >
            Quelques réponses honnêtes.
          </h2>
          <p style={{color: c.inkMuted, maxWidth: '36ch'}}>
            Des questions sur ce qui est inclus, le renouvellement ou
            l'adéquation à votre projet ?
          </p>
        </Reveal>

        <Reveal>
          <ul className="included-list m-0 list-none p-0">
            {faqs.map(([q, a]) => (
              <li
                key={q}
                className="grid grid-cols-1 items-start gap-8 py-26"
              >
                <p
                  className="text-lg"
                  style={{
                    fontWeight: 600,
                    color: c.ink,
                    margin: 0,
                    letterSpacing: 0,
                  }}
                >
                  {q}
                </p>
                <p
                  className="text-base"
                  style={{
                    color: c.ink2,
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  {a}
                </p>
              </li>
            ))}
          </ul>
        </Reveal>
      </Container>
    </section>
  );
}

/* ---------------- Final CTA ---------------- */

function PricingFinalCta() {
  return (
    <section
      className="border-t py-96 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="final-cta-title"
    >
      <Container>
        <Reveal className="mx-auto max-w-580 text-center">
          <h2
            id="final-cta-title"
            className="display mb-24 text-3xl md:text-5xl"
            style={{lineHeight: 1.1}}
          >
            Prêt à mettre votre site en ligne ?
          </h2>
          <p
            className="mx-auto mb-32 max-w-520 text-lg leading-7"
            style={{color: c.inkMuted}}
          >
            Une formule, sans engagement à vie. Si MRIGL.site est ce qu'il vous
            faut, c'est toute la discussion.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-x-24 gap-y-16">
            <Link
              to="/register"
              className="btn-lime inline-flex items-center gap-8 rounded px-28 py-16 text-base font-medium"
            >
              Créer mon site
              <ArrowRight />
            </Link>
            <Link
              to="/contact"
              className="btn-link inline-flex items-center gap-8 text-base font-medium"
            >
              Une question ?
              <ArrowRight />
            </Link>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
